<script lang="ts">
import { defineComponent, ref } from 'vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import type Address from '@/types/Address'
import type ResponseData from '@/types/ResponseData'
import AddressDataService from '@/services/AddressDataService'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import type Headquarters from '@/types/Headquarters'
import UserDataService from '@/services/UserDataService'
import type User from '@/types/User'
import type ObjectConfig from '@/types/ObjectConfig'
import type SubjectOfBusiness from '@/types/SubjectOfBusiness'
import SubjectOfBusinessDataService from '@/services/SubjectOfBusinessDataService'
import { buildSubjectsPayload } from '@/utils/companyDetail'
import CompanySubjectDialog from '@/components/company/CompanySubjectDialog.vue'
import CompanyBillingProfileDialog from '@/components/company/CompanyBillingProfileDialog.vue'
import CompanyEditDialog from '@/components/company/CompanyEditDialog.vue'
import CompanyAddressDialog from '@/components/company/CompanyAddressDialog.vue'
import CompanyBillingDialog from '@/components/company/CompanyBillingDialog.vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { describeRenewalBillingSource, profileLabel } from '@/utils/renewalBilling'
import type { RenewalBillingInfo } from '@/utils/renewalBilling'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableEmptyState } from '@/components/common/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardFooter } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Pencil } from 'lucide-vue-next'
import { companyTypeLabel, vhqPackageState, parseSubjects, type VhqPackageState, buildCompanyPayload, companyTypeFlags, hasLegacyCompanyData, buildAddressPayload, buildHeadquartersPayload } from '@/utils/companyDetail'

export default defineComponent({
  name: 'company-detail',
  components: {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableEmptyState,
    PageBreadcrumb,
    Button,
    Badge,
    Card,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    Pencil,
    CompanySubjectDialog,
    CompanyBillingProfileDialog,
    CompanyEditDialog,
    CompanyAddressDialog,
    CompanyBillingDialog,
  },
  data() {
    return {
      currentCompany: {} as Company,
      currentCompanyAddress: {} as Address,
      currentHeadquarters: {} as Headquarters,
      currentUser: {} as User,
      listOfUsers: [] as User[],
      companyStatuses: [] as ObjectConfig[],
      companySubStatuses: [] as ObjectConfig[],
      compantyTypes: [] as ObjectConfig[],
      konecnyUzivateliaVyhod: [] as ObjectConfig[],
      sposobyKonaniaKonatelov: [] as ObjectConfig[],
      headquartersTypes: [] as ObjectConfig[],
      subjectsOfBusiness: [] as SubjectOfBusiness[],
      listOfSubjects: [] as SubjectOfBusiness[],
      isSubjectDialogOpen: false,
      editingSubjectIndex: null as number | null,
      editingSubject: null as SubjectOfBusiness | null,
      savingSubjects: false,
      isDeleteDialogOpen: false,
      loaded: false,
      savingCompany: false,
      savingAddress: false,
      vhqs: [] as any[],
      showStatusInfoModal: false,
      billingProfiles: [] as any[],
      renewalBillingVhq: null as RenewalBillingInfo | null,
      renewalBillingDocuments: null as RenewalBillingInfo | null,
      renewalBillingFailed: false,
      showBillingProfileModal: false,
      isSavingBillingProfile: false,
      editingBillingProfileId: null as number | null,
      editingBillingAddressId: null as number | null,
      billingProfileTarget: 'vhq' as 'vhq' | 'documents',
      newBillingProfile: {
        name: '',
        first_name: '',
        last_name: '',
        ico: '',
        dic: '',
        ic_dph: '',
      },
      newBillingAddress: {
        street: '',
        street_number: '',
        city: '',
        psc: '',
        country: 'Slovensko',
      },
      selectedVhqAddressId: null as number | null,
      isCompanyEditOpen: false,
      isAddressEditOpen: false,
      isBillingEditOpen: false,
    }
  },
  methods: {
    onVhqLocationChange(addressId: any) {
      const id = Number(addressId)
      const vhq = this.vhqs.find((v: any) => Number(v.address_id) === id)
      if (vhq) {
        this.currentHeadquarters.owner_name = vhq.name
        this.currentHeadquarters.img = vhq.img
      }
      this.currentHeadquarters.address_id = id
      this.getCompanyAddress(id)
    },
    saveAddress() {
      if (this.savingAddress) return
      this.savingAddress = true
      AddressDataService.update(
        this.currentHeadquarters.address_id,
        buildAddressPayload(this.currentCompanyAddress),
      )
        .then(() =>
          HeadquartersDataService.update(
            this.currentHeadquarters.id,
            buildHeadquartersPayload(this.currentHeadquarters),
          ),
        )
        .then(() =>
          CompanyDataService.update(this.currentCompany.id, {
            is_virtual: this.currentHeadquarters.is_virtual,
          }),
        )
        .then(() => {
          this.currentCompany.is_virtual = this.currentHeadquarters.is_virtual
          toast.success('Adresa a sídlo boli uložené.')
        })
        .catch((e: Error) => toast.error('Chyba pri ukladaní adresy: ' + e.message))
        .finally(() => { this.savingAddress = false })
    },
    getAllUsers() {
      UserDataService.getAllUsers("all")
        .then((response: ResponseData) => {
          const users = response.data.data
          this.listOfUsers = Array.isArray(users) ? users : users?.data ?? []
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getAllVhqs() {
      HeadquartersDataService.getAllVirtualHeadquarters()
        .then((response: ResponseData) => {
          this.vhqs = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getCompanyStatuses() {
      CompanyDataService.getCompanyStatuses()
        .then((response: ResponseData) => {
          this.companyStatuses = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getCompanySubStatuses() {
      CompanyDataService.getCompanySubStatuses()
        .then((response: ResponseData) => {
          this.companySubStatuses = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getCompanyTypes() {
      CompanyDataService.getCompanyTypes()
        .then((response: ResponseData) => {
          this.compantyTypes = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getKonecnyUzivateliaVyhod() {
      CompanyDataService.getKonecnyUzivateliaVyhod()
        .then((response: ResponseData) => {
          this.konecnyUzivateliaVyhod = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getHeadquartersTypes() {
      HeadquartersDataService.getHeadquartersTypes()
        .then((response: ResponseData) => {
          this.headquartersTypes = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getSposobyKonaniaKonatelov() {
      CompanyDataService.getSposobyKonaniaKonatelov()
        .then((response: ResponseData) => {
          this.sposobyKonaniaKonatelov = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getUser(id: any) {
      return UserDataService.getUserById(id)
        .then((response: ResponseData) => {
          this.currentUser = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getCompanyAddress(id: any) {
      return AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
          this.currentCompanyAddress = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getCompanyHeadquarters(id: any) {
      return HeadquartersDataService.getHeadquartersById(id)
        .then((response: ResponseData) => {
          this.currentHeadquarters = response.data.data
          this.selectedVhqAddressId = this.currentHeadquarters.address_id ?? null
          return this.getCompanyAddress(this.currentHeadquarters.address_id)
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getCompany(id: any) {
      CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
          this.currentCompany = response.data.data
          this.currentCompany.vhq_renewal_fakturacne_udaje_id =
            this.currentCompany.vhq_renewal_fakturacne_udaje_id ?? null
          this.currentCompany.documents_renewal_fakturacne_udaje_id =
            this.currentCompany.documents_renewal_fakturacne_udaje_id ?? null
          this.subjectsOfBusiness = parseSubjects(this.currentCompany.subjects_of_business)
          this.loadBillingProfiles()
          this.loadRenewalBillingInfo()
          return Promise.all([
            this.getCompanyHeadquarters(this.currentCompany.headquarters_id),
            this.getUser(this.currentCompany.owner),
          ])
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
        .finally(() => {
          this.loaded = true
        })
    },
    loadBillingProfiles() {
      if (!this.currentCompany.owner) return
      UserDataService.getFakturacneUdajeByUserId(this.currentCompany.owner)
        .then((response: ResponseData) => {
          this.billingProfiles = response.data.data ?? []
        })
        .catch((e: Error) => {
          toast.error('Chyba pri načítaní fakturačných profilov: ' + e.message)
        })
    },
    loadRenewalBillingInfo() {
      this.renewalBillingFailed = false
      CompanyDataService.getRenewalBillingProfile(this.currentCompany.id, 'vhq')
        .then((response: ResponseData) => {
          this.renewalBillingVhq = response.data.data
        })
        .catch(() => {
          this.renewalBillingVhq = null
          this.renewalBillingFailed = true
        })
      CompanyDataService.getRenewalBillingProfile(this.currentCompany.id, 'documents')
        .then((response: ResponseData) => {
          this.renewalBillingDocuments = response.data.data
        })
        .catch(() => {
          this.renewalBillingDocuments = null
          this.renewalBillingFailed = true
        })
    },
    renewalBillingText(which: 'vhq' | 'documents') {
      if (this.renewalBillingFailed) return 'Info nedostupná — skúste obnoviť stránku'
      return describeRenewalBillingSource(which === 'vhq' ? this.renewalBillingVhq : this.renewalBillingDocuments)
    },
    billingProfileLabel(profile: any) {
      return profileLabel(profile)
    },
    openNewBillingProfile(target: 'vhq' | 'documents') {
      this.billingProfileTarget = target
      this.isSavingBillingProfile = false
      this.editingBillingProfileId = null
      this.editingBillingAddressId = null
      this.newBillingProfile = { name: '', first_name: '', last_name: '', ico: '', dic: '', ic_dph: '' }
      this.newBillingAddress = { street: '', street_number: '', city: '', psc: '', country: 'Slovensko' }
      this.showBillingProfileModal = true
    },
    openEditBillingProfile(target: 'vhq' | 'documents') {
      const overrideId = target === 'vhq'
        ? this.currentCompany.vhq_renewal_fakturacne_udaje_id
        : this.currentCompany.documents_renewal_fakturacne_udaje_id
      if (!overrideId) {
        toast.error('Najprv v dropdowne vyberte profil, ktorý chcete upraviť.')
        return
      }
      this.billingProfileTarget = target
      this.isSavingBillingProfile = false
      this.editingBillingProfileId = overrideId
      const known = this.billingProfiles.find((p: any) => p.id === overrideId)
      const applyProfile = (profile: any) => {
        this.newBillingProfile = {
          name: profile.name ?? '',
          first_name: profile.first_name ?? '',
          last_name: profile.last_name ?? '',
          ico: profile.ico ?? '',
          dic: profile.dic ?? '',
          ic_dph: profile.ic_dph ?? '',
        }
        this.editingBillingAddressId = profile.address_id ?? null
        if (profile.address_id) {
          AddressDataService.getAddressById(profile.address_id)
            .then((response: ResponseData) => {
              const a = response.data.data ?? {}
              this.newBillingAddress = {
                street: a.street ?? '',
                street_number: a.street_number ?? '',
                city: a.city ?? '',
                psc: a.psc ?? '',
                country: a.country ?? 'Slovensko',
              }
            })
            .catch((e: Error) => {
              toast.error('Chyba pri načítaní adresy profilu: ' + e.message)
            })
        }
        this.showBillingProfileModal = true
      }
      if (known) {
        applyProfile(known)
      } else {
        UserDataService.getFakturacneUdajeById(overrideId)
          .then((response: ResponseData) => applyProfile(response.data.data))
          .catch((e: Error) => {
            toast.error('Chyba pri načítaní profilu: ' + e.message)
          })
      }
    },
    createBillingProfile({ profile, address }: { profile: any; address: any }) {
      this.newBillingProfile = profile
      this.newBillingAddress = address
      if (this.isSavingBillingProfile) return
      this.isSavingBillingProfile = true
      if (this.editingBillingProfileId) {
        const addressId = this.editingBillingAddressId
        const updateAddress = addressId
          ? AddressDataService.update(addressId, this.newBillingAddress)
          : Promise.resolve(null)
        updateAddress
          .then(() => UserDataService.updateFakturacneUdaje(this.editingBillingProfileId, {
            ...this.newBillingProfile,
            user_id: this.currentCompany.owner,
            address_id: addressId,
          }))
          .then(() => UserDataService.getFakturacneUdajeByUserId(this.currentCompany.owner))
          .then((response: ResponseData) => {
            this.billingProfiles = response.data.data ?? []
            this.loadRenewalBillingInfo()
            this.showBillingProfileModal = false
            toast.success('Fakturačný profil upravený.')
          })
          .catch((e: Error) => {
            toast.error('Chyba pri úprave profilu: ' + e.message)
          })
          .finally(() => {
            this.isSavingBillingProfile = false
          })
        return
      }
      AddressDataService.add(this.newBillingAddress)
        .then((addressResponse: ResponseData) => {
          const addressId = addressResponse.data.address_id ?? addressResponse.data.id
          return UserDataService.addFakturacneUdaje({
            ...this.newBillingProfile,
            user_id: this.currentCompany.owner,
            address_id: addressId,
          })
        })
        .then((profileResponse: ResponseData) => {
          const newId = profileResponse.data.id
          return UserDataService.getFakturacneUdajeByUserId(this.currentCompany.owner)
            .then((response: ResponseData) => {
              this.billingProfiles = response.data.data ?? []
              if (this.billingProfileTarget === 'vhq') {
                this.currentCompany.vhq_renewal_fakturacne_udaje_id = newId
              } else {
                this.currentCompany.documents_renewal_fakturacne_udaje_id = newId
              }
              this.showBillingProfileModal = false
              toast.success('Fakturačný profil pridaný a predvybraný. Nezabudnite dať Uložiť.')
            })
        })
        .catch((e: Error) => {
          toast.error('Chyba pri pridaní profilu: ' + e.message)
        })
        .finally(() => {
          this.isSavingBillingProfile = false
        })
    },
    getAllSubjectsOfBusiness() {
      SubjectOfBusinessDataService.getAllSubjectsOfBusiness()
        .then((response: ResponseData) => {
          this.listOfSubjects = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    deleteCompany() {
      CompanyDataService.delete(this.currentCompany.id)
        .then((response: ResponseData) => {
          this.isDeleteDialogOpen = false
          this.$router.push({ name: 'Úprava spoločností' })
        })
        .catch((e: Error) => {
          this.isDeleteDialogOpen = false
          toast.error('Chyba: ' + e.message)
        })
    },
    openSubjectDialog(index: number | null) {
      this.editingSubjectIndex = index
      this.editingSubject = index === null
        ? null
        : { ...this.subjectsOfBusiness[index] }
      this.isSubjectDialogOpen = true
    },
    onSubjectSubmit(subject: SubjectOfBusiness) {
      if (this.editingSubjectIndex === null) {
        this.subjectsOfBusiness.push(subject)
      } else {
        this.subjectsOfBusiness[this.editingSubjectIndex] = subject
      }
      this.isSubjectDialogOpen = false
      this.persistSubjects()
    },
    removeSubject(index: number) {
      this.subjectsOfBusiness.splice(index, 1)
      this.persistSubjects()
    },
    persistSubjects() {
      if (this.savingSubjects) return
      this.savingSubjects = true
      const payload = buildSubjectsPayload(this.subjectsOfBusiness)
      CompanyDataService.update(this.currentCompany.id, payload)
        .then(() => {
          this.currentCompany.subjects_of_business = payload.subjects_of_business
          toast.success('Predmety podnikania boli uložené.')
        })
        .catch((e: Error) => toast.error('Chyba pri ukladaní predmetov: ' + e.message))
        .finally(() => { this.savingSubjects = false })
    },
    sendNotification(message: any) {
      const data = {
        email: this.currentUser.email,
        message: message,
      }
      UserDataService.sendNotification(data)
        .then(() => {
          toast.success('Notifikácia bola odoslaná.')
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    saveCompany() {
      if (this.savingCompany) return
      this.savingCompany = true
      CompanyDataService.update(this.currentCompany.id, buildCompanyPayload(this.currentCompany))
        .then(() => toast.success('Zmeny firmy boli uložené.'))
        .catch((e: Error) => toast.error('Chyba pri ukladaní firmy: ' + e.message))
        .finally(() => { this.savingCompany = false })
    },
    handleCompanySave(values: any) {
      this.currentCompany = { ...this.currentCompany, ...values }
      this.saveCompany()
      this.isCompanyEditOpen = false
    },
    handleAddressSave(values: any) {
      this.currentCompanyAddress = { ...this.currentCompanyAddress, ...values }
      this.currentHeadquarters = { ...this.currentHeadquarters, ...values }
      this.saveAddress()
      this.isAddressEditOpen = false
    },
    handleBillingSave(values: any) {
      this.currentCompany = { ...this.currentCompany, ...values }
      this.saveCompany()
      this.isBillingEditOpen = false
    },
    companyTypeLabel(typeName: string, description?: string | null): string {
      return companyTypeLabel(typeName, description)
    },
  },
  computed: {
    pageTitle(): string {
      return this.currentCompany.id
        ? `Detail spoločnosti – ${this.currentCompany.name || this.currentCompany.id} #${this.currentCompany.id}`
        : 'Detail spoločnosti'
    },
    statusLabel(): string {
      const status = this.companyStatuses.find(s => s.id === this.currentCompany.status)
      return status ? status.description || status.name : '—'
    },
    statusBadgeColor(): 'success' | 'gray' | 'warning' {
      if (this.currentCompany.status === 1) return 'success'
      if (this.currentCompany.status === 2) return 'gray'
      return 'warning'
    },
    typeLabel(): string {
      const type = this.compantyTypes.find(t => t.id === this.currentCompany.type)
      return type ? companyTypeLabel(type.name, type.description) : '—'
    },
    vhqState(): VhqPackageState {
      return vhqPackageState(this.currentCompany.sidlo_zaplatene_do, this.currentCompany.sidlo_deaktivovane)
    },
    typeFlags() {
      return companyTypeFlags(this.currentCompany.type)
    },
    hasLegacyData(): boolean {
      return hasLegacyCompanyData(this.currentCompany)
    },
    isDocumentsOverdue(): boolean {
      if (!this.currentCompany.fakturacia_zaplatene_do) return false
      const end = new Date(this.currentCompany.fakturacia_zaplatene_do)
      if (Number.isNaN(end.getTime())) return false
      end.setHours(23, 59, 59, 999)
      return end.getTime() < Date.now()
    },
  },
  mounted() {
    this.getAllUsers()
    this.getCompanyStatuses()
    this.getCompanySubStatuses()
    this.getCompanyTypes()
    this.getKonecnyUzivateliaVyhod()
    this.getSposobyKonaniaKonatelov()
    this.getHeadquartersTypes()
    this.getAllVhqs()
    this.getAllSubjectsOfBusiness()
    this.getCompany(this.$route.params.id)
  },
})
</script>

<template>
  <div>
    <PageBreadcrumb :page-title="pageTitle" :parent="{ label: 'Spoločnosti', to: '/companies' }" />

    <div class="flex items-center justify-end gap-3 mb-6">
      <Badge v-if="currentCompany.id" :color="statusBadgeColor">{{ statusLabel }}</Badge>
      <Button
        v-if="currentCompany.id"
        variant="destructive"
        size="sm"
        @click="isDeleteDialogOpen = true"
        >Zmazať</Button>
    </div>

    <div v-if="!loaded" class="flex justify-center py-20">
      <svg class="size-8 animate-spin text-brand-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
      </svg>
    </div>
    <div v-else class="space-y-6">
      <!-- Prehľad Card -->
      <Card data-size="default">
        <div class="flex items-center justify-between gap-3 mb-4">
          <h3 class="mb-0 text-lg font-semibold text-gray-800 dark:text-white/90">Prehľad</h3>
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Vlastník</div>
            <router-link
              v-if="currentCompany.owner"
              :to="{ name: 'Detail klienta', params: { id: currentCompany.owner } }"
              class="font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              {{ currentUser.first_name }} {{ currentUser.last_name }}
            </router-link>
            <div v-else class="font-medium text-gray-800 dark:text-white/90">Bez vlastníka</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">IČO / DIČ / IČ DPH</div>
            <div class="font-medium text-gray-800 dark:text-white/90">
              {{ currentCompany.ico || '—' }} · {{ currentCompany.dic || '—' }} · {{ currentCompany.icdph || '—' }}
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Sídlo</div>
            <div class="font-medium text-gray-800 dark:text-white/90">
              {{ currentHeadquarters.owner_name || '—' }}<span v-if="currentCompanyAddress.city">, {{ currentCompanyAddress.city }}</span>
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Typ</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ typeLabel }}</div>
          </div>
          <template v-if="currentHeadquarters.is_virtual">
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Balík</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.sidlo_typ_balika || '—' }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Zaplatené do</div>
              <div class="flex items-center gap-2">
                <span class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.sidlo_zaplatene_do || '—' }}</span>
                <Badge
                  :color="vhqState === 'active' ? 'success' : vhqState === 'deactivated' ? 'gray' : 'error'"
                >
                  {{
                    vhqState === 'active'
                      ? 'Aktívne'
                      : vhqState === 'deactivated'
                        ? 'Deaktivované'
                        : 'Expirované'
                  }}
                </Badge>
              </div>
            </div>
          </template>
        </div>
      </Card>

      <!-- Firma Card -->
      <Card data-size="default">
        <div class="flex items-center justify-between gap-3 mb-4">
          <h3 class="mb-0 text-lg font-semibold text-gray-800 dark:text-white/90">Firma</h3>
          <Button variant="outline" size="sm" class="h-9 gap-2 px-3" @click="isCompanyEditOpen = true">
            <Pencil class="size-4.5" />
            <span>Upraviť</span>
          </Button>
        </div>
        <div class="space-y-4">
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Poznámka</div>
            <div class="font-medium text-gray-800 dark:text-white/90 whitespace-pre-wrap">{{ currentCompany.note || '—' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">{{ typeFlags.isFyzos ? 'Meno a priezvisko' : 'Názov spoločnosti' }}</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.name || '—' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Typ spoločnosti</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ typeLabel }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Stav spoločnosti</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.status }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Stav žiadosti</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.sub_status }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Účet spoločnosti (správca)</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.owner || '—' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">DIČ</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.dic || '—' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">IČ DPH</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.icdph || '—' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Začiatok oprávnenia</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.zaciatok_opravnenia || '—' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">IČO / Rodné číslo</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.ico || '—' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Je platiteľ DPH?</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.is_dph ? 'Áno' : 'Nie' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Skryť zo UI</div>
            <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.hide_from_ui ? 'Áno' : 'Nie' }}</div>
          </div>
          <template v-if="typeFlags.isSro">
            <div class="md:col-span-2">
              <div class="text-sm text-gray-500 dark:text-gray-400">Základné ímanie</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.imanie_vyska || '—' }}</div>
            </div>
            <div class="md:col-span-2">
              <div class="text-sm text-gray-500 dark:text-gray-400">Splatené ímanie</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.imanie_splatene || '—' }}</div>
            </div>
            <div class="md:col-span-2">
              <div class="text-sm text-gray-500 dark:text-gray-400">Spôsob konania konateľov</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.sposob_konania_konatelov || '—' }}</div>
            </div>
            <div class="md:col-span-2">
              <div class="text-sm text-gray-500 dark:text-gray-400">Spôsob konania – detaily</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.skk_details || '—' }}</div>
            </div>
            <div class="md:col-span-2">
              <div class="text-sm text-gray-500 dark:text-gray-400">Konečný užívateľia výhod</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.konecny_uzivatelia_vyhod || '—' }}</div>
            </div>
            <div class="md:col-span-2">
              <div class="text-sm text-gray-500 dark:text-gray-400">KUV – detaily</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.kuv_details || '—' }}</div>
            </div>
          </template>
          <template v-if="typeFlags.isZivnost || typeFlags.isFyzos">
            <details class="md:col-span-2 border border-gray-200 rounded-lg">
              <summary class="flex items-center justify-between p-3 cursor-pointer list-none hover:bg-gray-50">
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Ďalšie údaje
                  <span class="text-xs text-gray-500">({{ hasLegacyData ? 'obsahuje údaje' : 'prázdne' }})</span></span>
                <span class="text-xs text-gray-400">iba s.r.o. polia</span>
              </summary>
              <div class="p-4">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">Základné ímanie</div>
                    <div class="font-medium text-gray-500 dark:text-gray-400 opacity-60">{{ currentCompany.imanie_vyska || '—' }}</div>
                  </div>
                  <div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">Splatené ímanie</div>
                    <div class="font-medium text-gray-500 dark:text-gray-400 opacity-60">{{ currentCompany.imanie_splatene || '—' }}</div>
                  </div>
                  <div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">Spôsob konania konateľov</div>
                    <div class="font-medium text-gray-500 dark:text-gray-400 opacity-60">{{ currentCompany.sposob_konania_konatelov || '—' }}</div>
                  </div>
                  <div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">Spôsob konania – detaily</div>
                    <div class="font-medium text-gray-500 dark:text-gray-400 opacity-60">{{ currentCompany.skk_details || '—' }}</div>
                  </div>
                  <div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">Konečný užívateľia výhod</div>
                    <div class="font-medium text-gray-500 dark:text-gray-400 opacity-60">{{ currentCompany.konecny_uzivatelia_vyhod || '—' }}</div>
                  </div>
                  <div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">KUV – detaily</div>
                    <div class="font-medium text-gray-500 dark:text-gray-400 opacity-60">{{ currentCompany.kuv_details || '—' }}</div>
                  </div>
                </div>
              </div>
            </details>
          </template>
        </div>
      </Card>

      <CompanyEditDialog
        :is-open="isCompanyEditOpen"
        :initial-data="currentCompany"
        :list-of-users="listOfUsers"
        :companty-types="compantyTypes"
        :company-statuses="companyStatuses"
        :company-sub-statuses="companySubStatuses"
        :sposoby-konania-konatelov="sposobyKonaniaKonatelov"
        :konecny-uzivatelia-vyhod="konecnyUzivateliaVyhod"
        @open-change="isCompanyEditOpen = $event"
        @save="handleCompanySave"
      />

      <!-- Adresa a sídlo + Virtuálne sídlo Cards (2-col grid) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-size="default">
        <div class="flex items-center justify-between gap-3 mb-4">
          <h3 class="mb-0 text-lg font-semibold text-gray-800 dark:text-white/90">Adresa a sídlo</h3>
          <Button variant="outline" size="sm" class="h-9 gap-2 px-3" @click="isAddressEditOpen = true">
            <Pencil class="size-4.5" />
            <span>Upraviť</span>
          </Button>
        </div>
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-500 dark:text-gray-400">Naše virtuálne sídlo</span>
            <span class="font-medium text-gray-800 dark:text-white/90">{{ currentHeadquarters.is_virtual ? 'Áno' : 'Nie' }}</span>
          </div>
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Typ sídla</div>
            <div class="font-medium text-gray-800 dark:text-white/90">
              {{ headquartersTypes.find((ht: any) => ht.id === currentHeadquarters.headquarters_type)?.name || '—' }}
            </div>
            <p v-if="currentHeadquarters.headquarters_type" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {{ headquartersTypes.find((ht: any) => ht.id === currentHeadquarters.headquarters_type)?.description || '' }}
            </p>
          </div>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Ulica</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompanyAddress.street || '—' }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Súpisné číslo</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompanyAddress.street_number || '—' }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Orientačné číslo</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompanyAddress.street_number2 || '—' }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Mesto</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompanyAddress.city || '—' }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">PSČ</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompanyAddress.psc || '—' }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Krajina</div>
              <div class="font-medium text-gray-800 dark:text-white/90">{{ currentCompanyAddress.country || '—' }}</div>
            </div>
          </div>
          <template v-if="currentHeadquarters.is_virtual">
            <div>
              <div class="text-sm text-gray-500 dark:text-gray-400">Virtuálne sídlo (lokácia)</div>
              <div class="font-medium text-gray-800 dark:text-white/90">
                {{ vhqs.find((v: any) => v.address_id === selectedVhqAddressId)?.name || '—' }}
              </div>
            </div>
          </template>
        </div>
      </Card>

      <CompanyAddressDialog
        :is-open="isAddressEditOpen"
        :initial-data="{ ...currentHeadquarters, ...currentCompanyAddress, selectedVhqAddressId }"
        :headquarters-types="headquartersTypes"
        :vhqs="vhqs"
        @open-change="isAddressEditOpen = $event"
        @save="handleAddressSave"
      />

      <!-- Virtuálne sídlo Card -->
      <Card v-if="currentHeadquarters.is_virtual" data-size="default">
        <div class="flex items-center justify-between gap-3 mb-4">
          <h3 class="mb-0 text-lg font-semibold text-gray-800 dark:text-white/90">Virtuálne sídlo</h3>
        </div>
        <div class="space-y-4">
          <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt class="text-sm text-gray-500 dark:text-gray-400">Balík</dt>
              <dd class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.sidlo_typ_balika || '—' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-gray-500 dark:text-gray-400">Zaplatené do</dt>
              <dd class="font-medium text-gray-800 dark:text-white/90">{{ currentCompany.sidlo_zaplatene_do || '—' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-gray-500 dark:text-gray-400">Cena</dt>
              <dd class="font-medium text-gray-800 dark:text-white/90">{{ currentHeadquarters.price ?? '—' }} €</dd>
            </div>
            <div>
              <dt class="text-sm text-gray-500 dark:text-gray-400">Lokalita</dt>
              <dd class="font-medium text-gray-800 dark:text-white/90">{{ currentHeadquarters.owner_name || '—' }}</dd>
            </div>
          </dl>
          <div class="flex flex-wrap gap-2">
            <Badge
              v-if="currentHeadquarters.picking_up_mails"
              color="info"
            >
              Preberanie zásielok
            </Badge>
            <Badge
              v-if="currentHeadquarters.e_data_mail_sk"
              color="info"
            >
              eDataMail.sk
            </Badge>
            <Badge
              v-if="currentHeadquarters.active_on_address"
              color="info"
            >
              Aktívne na adrese
            </Badge>
            <Badge
              v-if="currentHeadquarters.marked_mailbox"
              color="info"
            >
              Označená schránka
            </Badge>
          </div>
        </div>
        <div class="mt-2">
          <router-link
            :to="{ name: 'VHQ Detail', params: { id: currentCompany.id } }"
            class="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            Otvoriť VHQ detail →
          </router-link>
        </div>
      </Card>
      </div>

      <!-- Fakturácia a obnovy Card -->
      <Card data-size="default">
        <div class="flex items-center justify-between gap-3 mb-4">
          <h3 class="mb-0 text-lg font-semibold text-gray-800 dark:text-white/90">Fakturácia a obnovy</h3>
          <Button variant="outline" size="sm" class="h-9 gap-2 px-3" @click="isBillingEditOpen = true">
            <Pencil class="size-4.5" />
            <span>Upraviť</span>
          </Button>
        </div>
        <div class="space-y-4">
          <div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Balík zaplatený do:</div>
            <div class="font-medium text-gray-800 dark:text-white/90"
              :class="isDocumentsOverdue ? 'text-error' : ''">
              {{ currentCompany.fakturacia_zaplatene_do || '—' }}
            </div>
            <div v-if="isDocumentsOverdue" class="flex items-center gap-2 mt-2">
              <Badge color="error">Expirované</Badge>
              <Button variant="outline" size="sm" @click="sendNotification('Platnosť Vášho balíka pre Fakturáciu vypršala. Zakúpte si predĺženie už dnes a získajte mnoho výhod! :)')">
                Odoslať notifikáciu
              </Button>
            </div>
          </div>

          <div v-if="currentCompany.fakturacia_free" class="font-bold mt-6 text-xl text-warning">
            !! Trial balík je aktívny !!
          </div>

          <div class="flex flex-row gap-4 pt-2">
            <div class="flex flex-col">
              <div class="text-sm text-gray-500 dark:text-gray-400">Fakturačný profil pre auto-obnovu Dokladov</div>
              <div class="font-medium text-gray-800 dark:text-white/90">
                {{ billingProfiles.find((p: any) => p.id === currentCompany.documents_renewal_fakturacne_udaje_id)?.name || 'Predvolené (posledná uhradená objednávka)' }}
              </div>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ renewalBillingText('documents') }}</p>
            </div>
          </div>

          <div class="flex flex-row gap-4 pt-2">
            <div class="flex flex-col">
              <div class="text-sm text-gray-500 dark:text-gray-400">Fakturačný profil pre auto-obnovu sídla</div>
              <div class="font-medium text-gray-800 dark:text-white/90">
                {{ billingProfiles.find((p: any) => p.id === currentCompany.vhq_renewal_fakturacne_udaje_id)?.name || 'Predvolené (posledná uhradená objednávka)' }}
              </div>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ renewalBillingText('vhq') }}</p>
            </div>
          </div>
        </div>
      </Card>

      <CompanyBillingDialog
        :is-open="isBillingEditOpen"
        :initial-data="{ fakturacia_zaplatene_do: currentCompany.fakturacia_zaplatene_do, documents_renewal_fakturacne_udaje_id: currentCompany.documents_renewal_fakturacne_udaje_id, vhq_renewal_fakturacne_udaje_id: currentCompany.vhq_renewal_fakturacne_udaje_id }"
        :billing-profiles="billingProfiles"
        @open-change="isBillingEditOpen = $event"
        @save="handleBillingSave"
      />

      <!-- Predmety podnikania -->
      <TableCard title="Predmety podnikania">
        <template #controls>
          <Button size="sm" @click="openSubjectDialog(null)">
            Pridať predmet
          </Button>
        </template>
        <div class="max-w-full overflow-x-auto min-w-0">
          <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
            <TableHeader class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900">
              <TableRow>
                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Názov</TableCell>
                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Popis</TableCell>
                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Cena</TableCell>
                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Kategória</TableCell>
                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">UI kategória</TableCell>
                <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Akcie</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
              <tr v-for="(subject, index) in subjectsOfBusiness" :key="index"
                class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">
                <td class="px-6 py-4">{{ subject.title }}</td>
                <td class="px-6 py-4">{{ subject.description }}</td>
                <td class="px-6 py-4">{{ subject.price }}</td>
                <td class="px-6 py-4">{{ subject.category_id }}</td>
                <td class="px-6 py-4">{{ subject.ui_category_id }}</td>
                <td class="px-6 py-4">
                  <Button variant="ghost" size="xs" @click="openSubjectDialog(index)">Upraviť</Button>
                  <Button variant="ghost" size="xs" @click="removeSubject(index)">Zmazať</Button>
                </td>
              </tr>
              <TableEmptyState
                v-if="subjectsOfBusiness.length === 0"
                title="Žiadne predmety podnikania"
                hint="Pridať prvý predmet cez tlačidlo vyššie"
                colspan="6"
              />
            </TableBody>
          </Table>
        </div>
      </TableCard>
    </div>
  </div>

  <CompanySubjectDialog
    v-model:open="isSubjectDialogOpen"
    :subject="editingSubject"
    :catalog="listOfSubjects"
    :saving="savingSubjects"
    @submit="onSubjectSubmit"
  />

  <CompanyBillingProfileDialog
    v-model:open="showBillingProfileModal"
    :target="billingProfileTarget"
    :profile="newBillingProfile"
    :address="newBillingAddress"
    :editing="Boolean(editingBillingProfileId)"
    :saving="isSavingBillingProfile"
    @submit="createBillingProfile"
  />

  <Dialog v-model:open="isDeleteDialogOpen">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>Zmazanie firmy</DialogTitle>
        <DialogDescription>
          Naozaj chcete zmazať túto firmu? Je to nenávratné :(
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" @click="isDeleteDialogOpen = false">Zrušiť</Button>
        <Button variant="destructive" @click="deleteCompany">Zmazať</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <Dialog v-model:open="showStatusInfoModal">
    <DialogContent class="max-w-lg">
      <DialogHeader>
        <DialogTitle>Popis stavov žiadosti</DialogTitle>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" @click="showStatusInfoModal = false">Zavrieť</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>